import {
  AHB_MESSAGE_TYPE,
  AHB_PROCEDURE_ID,
  AHB_SUBPROCEDURE,
  RECARE_FILE_TYPE,
  REHAB_FORM_NAMES,
  TRACK_EVENTS,
} from "core/consts";
import { ENV_PRODUCTION } from "core/model/config";
import { extractInvalidFields } from "core/schemas/utils";
import {
  AHBMessageType,
  AHBProcedureId,
  AHBProcessingCode,
  AuctionForm,
  Env,
  FileState,
  GKVGeneralFormData,
  GKVMedicalFormData,
  RehabFormName,
  TrackEventFn,
} from "core/types";
import { RehabFormDataMapped } from "dsl/ecosystems/PatientForms/RehabForms/utils";
import { convertEncryptedFieldIn } from "react-forms-state";
import { create } from "xmlbuilder2";
import { z } from "zod";
import { getAHBRequestPayloadSchema } from "../core/schemas/AHBRequestSchema";
import { mapFormToRehabRequest } from "./mapping/mapFormToRehabRequest";
import { resolveArhVersion } from "./resolveArhVersion";

type NeedsIndicator<MT extends AHBMessageType> = MT extends
  | typeof AHB_MESSAGE_TYPE.REQUEST
  | typeof AHB_MESSAGE_TYPE.RESPONSE
  ? AHBProcessingCode
  : undefined;

export async function createValidatedXml<
  T extends z.ZodType<Record<string, unknown>>,
>({
  formName,
  json,
  onInvalid,
  schema,
  shouldValidate,
  trackEvent,
}: {
  json: z.infer<T>;
  onInvalid?: () => void;
  schema: T;
  shouldValidate?: boolean;
  trackEvent: TrackEventFn;
  formName?: RehabFormName;
}) {
  if (shouldValidate) {
    const result = schema.safeParse(json);

    if (!result.success) {
      onInvalid?.();
      console.log("[createValidatedXml] JSON:", json);

      const invalidFields = extractInvalidFields(result.error.issues);
      console.error(
        "Validation failed with the following issues:",
        invalidFields,
      );

      trackEvent({
        formName,
        invalidFields,
        name: TRACK_EVENTS.AHB_XML_VALIDATION_FAILED,
        totalIssues: result.error.issues.length,
      });

      const groupedIssues: Record<string, string[]> = {};

      result.error.issues.forEach((issue) => {
        const [group, ...rest] = issue.path;
        const key = String(group);
        const subPath = rest.join(".");
        const fullMessage = subPath
          ? `${subPath}: ${issue.message}`
          : issue.message;

        if (!groupedIssues[key]) {
          groupedIssues[key] = [];
        }

        groupedIssues[key].push(fullMessage);
      });

      Object.entries(groupedIssues).forEach(([group, messages]) => {
        console.error(`- ${group}:`);
        messages.forEach((msg) => {
          console.error(`    • ${msg}`);
        });
      });

      throw new Error("[createValidatedXml] Schema validation failed");
    }
  }

  const doc = create({
    encoding: "UTF-8",
    standalone: true,
    version: "1.0",
  }).ele(json);
  const xml = doc.end({ prettyPrint: true });

  if (!xml) {
    throw new Error("[createValidatedXml] Failed to generate XML");
  }

  return { json, xml };
}

export async function createAHBRequestXml({
  auctionForms,
  documentNumber,
  env,
  excludePatientAddress,
  externalId,
  files,
  formData,
  formName,
  hospitalIkNumber,
  insuranceIkNumber,
  on_premise_authorization_token,
  on_premise_domain_ssl,
  onInvalid,
  shouldValidate,
  trackEvent,
}: {
  env: Env;
  excludePatientAddress?: boolean;
  externalId: string;
  files?: FileState[];
  formName?: RehabFormName;
  auctionForms: AuctionForm[];
  formData?: RehabFormDataMapped<RehabFormName>;
  hospitalIkNumber?: number;
  insuranceIkNumber?: number;
  on_premise_authorization_token?: string;
  on_premise_domain_ssl?: string;
  onInvalid?: () => void;
  shouldValidate?: boolean;
  trackEvent: TrackEventFn;
  documentNumber: number;
}) {
  let derivedFormData = formData;

  if (!derivedFormData && auctionForms?.length) {
    const generalForm = auctionForms.find(
      (f) =>
        f.form_type === RECARE_FILE_TYPE.GKV_GENERAL_FORM ||
        f.form_type === RECARE_FILE_TYPE.DRV_GENERAL_FORM,
    );
    const medicalForm = auctionForms.find(
      (f) =>
        f.form_type === RECARE_FILE_TYPE.GKV_MEDICAL_FORM ||
        f.form_type === RECARE_FILE_TYPE.DRV_MEDICAL_FORM,
    );

    const generalFormData: GKVGeneralFormData = generalForm?.form_data
      ? (JSON.parse(
          convertEncryptedFieldIn(generalForm.form_data) ?? "{}",
        ) as GKVGeneralFormData)
      : {};

    const medicalFormData: GKVMedicalFormData = medicalForm?.form_data
      ? (JSON.parse(
          convertEncryptedFieldIn(medicalForm.form_data) ?? "{}",
        ) as GKVMedicalFormData)
      : {};

    if (formName === REHAB_FORM_NAMES.GKV_GENERAL) {
      derivedFormData = generalFormData;
    } else if (formName === REHAB_FORM_NAMES.GKV_MEDICAL) {
      derivedFormData = medicalFormData;
    } else {
      // data is merged for AHB requests
      derivedFormData = {
        ...generalFormData,
        ...medicalFormData,
      };
    }
  }

  if (derivedFormData) {
    const data = derivedFormData as Record<string, unknown>;
    if (hospitalIkNumber) {
      data.hospital_ik_number = String(hospitalIkNumber);
    }
    if (insuranceIkNumber) {
      data.insurance_ik_number = String(insuranceIkNumber);
    }
  }

  const schemaVersion = resolveArhVersion(auctionForms);

  const json = await mapFormToRehabRequest({
    documentNumber,
    excludePatientAddress,
    externalId,
    files,
    isProd: env === ENV_PRODUCTION,
    on_premise_authorization_token,
    on_premise_domain_ssl,
    schemaVersion,
    unsanitizedForm: derivedFormData as GKVGeneralFormData & GKVMedicalFormData,
  });

  const schema = getAHBRequestPayloadSchema(schemaVersion);

  const { json: validatedJson, xml } = await createValidatedXml({
    formName,
    json: json as z.infer<typeof schema>,
    onInvalid,
    schema,
    shouldValidate,
    trackEvent,
  });

  return { json: validatedJson, schemaVersion, xml };
}

export function buildAarFilename<MT extends AHBMessageType>({
  message_id,
  message_type,
  procedure_id,
  processing_code,
}: {
  message_id: string;
  message_type: MT;
  procedure_id: AHBProcedureId;
} & ([NeedsIndicator<MT>] extends [undefined]
  ? // Intentional: omit processing_code when NeedsIndicator is undefined
    // eslint-disable-next-line sonarjs/no-redundant-optional
    { processing_code?: undefined }
  : { processing_code: NeedsIndicator<MT> })) {
  if (!Object.values(AHB_PROCEDURE_ID).includes(procedure_id)) {
    throw new Error(`Invalid procedure_id: ${procedure_id}`);
  }
  if (message_type.length !== 3) {
    throw new Error(`message_type must be 3 chars: got "${message_type}"`);
  }
  if (message_id.length < 1 || message_id.length > 36) {
    throw new Error(
      `message_id must be 1–36 chars: length ${message_id.length}`,
    );
  }
  if (processing_code !== undefined) {
    if (
      message_type === AHB_MESSAGE_TYPE.WITHDRAWAL ||
      message_type === AHB_MESSAGE_TYPE.ERROR
    ) {
      throw new Error(
        `processing_code not allowed for message_type ${message_type}`,
      );
    }
    if (processing_code.length !== 2) {
      throw new Error(`processing_code must be exactly 2 chars`);
    }
  }

  const baseName = [
    procedure_id,
    AHB_SUBPROCEDURE,
    message_type,
    message_id,
  ].join("_");

  return processing_code ? `${baseName}_${processing_code}` : `${baseName}`;
}
